import type { ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { cn } from "@blog/ui";

import { useApi } from "./ApiContext";
import type { CmsApiClient } from "../lib/api";
import { getLocale, locales, setLocale, t } from "../lib/i18n";

/**
 * The Admin shell: navigation plus the outlet every page renders into.
 *
 * The route list mirrors SPEC §38. Entries whose pages are not built yet are
 * still listed — a menu that grows as phases land is easier to reason about
 * than one that hides what exists.
 */

interface NavEntry {
  readonly to: string;
  readonly label: string;
  /** True for a route that exists but has no page yet. */
  readonly pending?: boolean;
}

const NAVIGATION: readonly NavEntry[] = [
  { to: "/dashboard", label: t("nav.dashboard") },
  { to: "/articles", label: t("nav.articles") },
  { to: "/pages", label: t("nav.pages") },
  { to: "/taxonomy", label: t("nav.taxonomy") },
  { to: "/media", label: t("nav.media") },
  { to: "/comments", label: t("nav.comments") },
  { to: "/themes", label: t("nav.themes") },
  { to: "/deployments", label: t("nav.deployments") },
  { to: "/settings/github", label: t("nav.github") },
  { to: "/settings/llm", label: t("nav.llm") },
];

export function Shell() {
  const client = useApi();

  return (
    <div className="flex min-h-screen bg-neutral-50">
      <aside className="flex w-56 shrink-0 flex-col border-r border-neutral-200 bg-white">
        <div className="border-b border-neutral-200 p-4">
          <p className="text-sm font-semibold text-neutral-900">{t("app.title")}</p>
          <p className="text-xs text-neutral-500">{t("app.subtitle")}</p>
        </div>

        <nav className="p-2" aria-label="Main">
          <ul className="flex flex-col gap-0.5">
            {NAVIGATION.map((entry) => (
              <li key={entry.to}>
                <NavLink
                  to={entry.to}
                  className={({ isActive }: { isActive: boolean }) =>
                    cn(
                      "block rounded-md px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900",
                      isActive && "bg-neutral-100 font-medium text-neutral-900",
                    )
                  }
                >
                  {entry.label}
                  {entry.pending === true ? (
                    <span className="ml-1.5 text-xs text-neutral-400">{t("nav.soon")}</span>
                  ) : null}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-auto flex flex-col gap-2 p-4">
          <select
            className="h-8 w-full rounded-md border border-neutral-300 bg-white px-2 text-xs"
            aria-label={t("nav.language")}
            value={getLocale()}
            onChange={(event) => {
              setLocale(event.target.value as ReturnType<typeof getLocale>);
            }}
          >
            {locales().map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="h-8 w-full rounded-md border border-neutral-300 bg-white px-2 text-xs text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
            onClick={() => {
              void signOut(client);
            }}
          >
            {t("nav.signOut")}
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Sign out of whichever mechanism authenticated this browser.
 *
 * The development-auth credential lives in localStorage, so removing it is the
 * whole sign-out. Under Cloudflare Access the session is a server-side cookie
 * this script cannot clear — only Access's own logout endpoint can, and the
 * `/cdn-cgi/access/logout` path is served by Access on the app's own origin.
 * Which one applies is answered by `/api/auth/me`, not guessed from config.
 */
async function signOut(client: CmsApiClient): Promise<void> {
  localStorage.removeItem("cms-dev-auth-secret");

  try {
    const user = await client.getCurrentUser();

    if (user.via === "cloudflare-access") {
      window.location.href = "/cdn-cgi/access/logout";
      return;
    }
  } catch {
    // Already unauthenticated — the reload below lands on the sign-in prompt.
  }

  window.location.reload();
}

/** A page heading with an optional action on the right. */
export interface PageHeaderProps {
  readonly title: string;
  readonly description?: string;
  readonly children?: ReactNode;
}

export function PageHeader({ title, description, children }: PageHeaderProps) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 className="text-xl font-semibold text-neutral-900">{title}</h1>
        {description === undefined ? null : (
          <p className="mt-1 text-sm text-neutral-500">{description}</p>
        )}
      </div>
      {children === undefined ? null : <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}
